/**
 * 對話頁視圖（UI v2 步 7：由 `ChatPage.tsx` 抽出，零行為改動）
 *
 *   頂欄（＋新對話／模式切換／之前嘅對話）＋ 對話串 ＋ 提示列 ＋ 歌結果卡
 *   ＋ 〔繼續傾〕＋ 錯誤 ＋ 輸入框 ＋ 全部彈窗。
 *
 * ⚠️ 全部狀態／處理器由 `ChatController` 傳入 —— 本檔**只搬 JSX**。
 */
import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { StyleOption } from '@coeldery/style-picker';
import ScreenTopBar from '@coeldery/screen-top-bar';
import SegmentedSwitch from '@coeldery/segmented-switch';
import ChatThread from '@coeldery/chat-thread';
import EmptyState from '@coeldery/empty-state';
import ChatComposer from '@coeldery/chat-composer';
import type { useChatStream } from '../../hooks/useChatStream';
import type { useSongFlow } from '../../hooks/useSongFlow';
import type { MustUseItem, MustUseMode } from './useMustUse';
import type { createMessageRenderers } from './messageActions';
import type { ChatMode, HistoryItem, SheetKind, SongLang, SongRegister } from './types';
import { ICON_BTN, ROW_BTN } from './styles';
import ChatSheets from './ChatSheets';
import SongResult from './SongResult';
import GeneratingCard from '../song/GeneratingCard';
import { ChatHints, ContinuePrompt } from './ContinuePrompt';

export interface ChatController {
  t: TFunction;
  state: ReturnType<typeof useChatStream>['state'];
  retry: () => void;
  flow: ReturnType<typeof useSongFlow>;
  deviceId: string;
  sessionId: string;
  openSession: (id: string) => void;
  startNewChat: () => void;
  onLibraryReload: () => void;
  pinned: MustUseItem[];
  setPinned: Dispatch<SetStateAction<MustUseItem[]>>;
  armed: MustUseMode | null;
  setArmed: Dispatch<SetStateAction<MustUseMode | null>>;
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  handleSend: () => void;
  openStartSong: () => void;
  mode: ChatMode;
  setMode: (m: ChatMode) => void;
  songLang: SongLang;
  setSongLang: (l: SongLang) => void;
  langOptions: { code: string; label: string }[];
  songRegister: SongRegister;
  setSongRegister: (r: SongRegister) => void;
  registerOptions: { code: string; label: string }[];
  songGenre: string | null;
  pickGenre: (id: string | null) => void;
  genreOptions: StyleOption[];
  genreQuick: string[];
  toggleQuick: (id: string) => void;
  styleOptions: StyleOption[];
  styleSelected: string[];
  setStyleSelected: Dispatch<SetStateAction<string[]>>;
  styleFree: string;
  setStyleFree: (v: string) => void;
  sheet: SheetKind;
  setSheet: (s: SheetKind) => void;
  runUnderstand: () => void;
  runRemake: () => void;
  history: HistoryItem[];
  historyBusy: boolean;
  historyBusyId: string | null;
  historyError: string | null;
  setHistoryError: (v: string | null) => void;
  renameTarget: { id: string; title: string } | null;
  setRenameTarget: Dispatch<SetStateAction<{ id: string; title: string } | null>>;
  deleteTarget: HistoryItem | null;
  setDeleteTarget: (v: HistoryItem | null) => void;
  openHistory: () => void;
  commitRename: () => void;
  commitDelete: () => void;
  playlistSongId: string | null;
  setPlaylistSongId: (v: string | null) => void;
  busyAction: boolean;
  renderingId: string | null;
  actionError: string | null;
  songFinished: boolean;
  keepGoing: boolean;
  setKeepGoing: Dispatch<SetStateAction<boolean>>;
  hasConversation: boolean;
  exitEdit: () => void;
  playSong: (songId: string) => void;
  langBtn: (active: boolean) => string;
  renderExtra: ReturnType<typeof createMessageRenderers>['renderExtra'];
  messageActions: ReturnType<typeof createMessageRenderers>['messageActions'];
}

export default function ChatView({ c }: { c: ChatController }) {
  const {
    t, state, retry, flow, deviceId, sessionId, openSession, startNewChat, onLibraryReload,
    pinned, setPinned, armed, setArmed, input, setInput, handleSend, openStartSong,
    mode, setMode, songLang, setSongLang, langOptions, songRegister, setSongRegister, registerOptions,
    songGenre, pickGenre, genreOptions, genreQuick, toggleQuick,
    styleOptions, styleSelected, setStyleSelected, styleFree, setStyleFree,
    sheet, setSheet, runUnderstand, runRemake,
    history, historyBusy, historyBusyId, historyError, setHistoryError,
    renameTarget, setRenameTarget, deleteTarget, setDeleteTarget,
    openHistory, commitRename, commitDelete, playlistSongId, setPlaylistSongId,
    busyAction, renderingId, actionError, songFinished, keepGoing, setKeepGoing, hasConversation,
    exitEdit, playSong, langBtn, renderExtra, messageActions,
  } = c;

  /**
   * ⭐ UI v2 步 13（設計 C05／**R-01**）：生成階段 **前端佔位** ——
   *   後端未回 `stage`，按經過時間粗略推估（0–3）；⚠️ 唔准當真、**無倒數**。
   */
  const [genStage, setGenStage] = useState(0);
  useEffect(() => {
    if (!renderingId) {
      setGenStage(0);
      return;
    }
    const t0 = Date.now();
    const id = setInterval(() => setGenStage(Math.min(3, Math.floor((Date.now() - t0) / 45000))), 5000);
    return () => clearInterval(id);
  }, [renderingId]);

  return (
    <div className="page">
      {/* 頂欄：左〔＋〕新對話／右〔之前嘅對話〕 */}
      <ScreenTopBar
        left={
          <button
            type="button"
            aria-label={t('topbar_new_chat_aria')}
            title={t('topbar_new_chat_aria')}
            disabled={state.pending || busyAction}
            onClick={() => {
              setKeepGoing(false);
              setArmed(null);
              exitEdit();
              startNewChat();
            }}
            style={ICON_BTN}
          >
            ＋
          </button>
        }
        center={
          /* ⭐ `decisions/027`：模式切換（撳左＝聆聽者／撳右＝聊天）—— 產品負責人指定要「自己撳」 */
          <SegmentedSwitch
            labelKey="mode_switch_aria"
            value={mode}
            onChange={(id) => setMode(id === 'chat' ? 'chat' : 'listener')}
            options={[
              { id: 'listener', label: t('mode_listener') },
              { id: 'chat', label: t('mode_chat') },
            ]}
            busy={state.pending}
          />
        }
        right={
          <button
            type="button"
            data-testid="open-history"
            aria-label={t('history_title')}
            title={t('history_title')}
            onClick={() => void openHistory()}
            style={ICON_BTN}
          >
            🕘
          </button>
        }
      />

      <div className="page__scroll">
        {!state.hydrated ? (
          <p className="page__loading">{t('chat_thinking')}</p>
        ) : state.messages.length === 0 ? (
          <EmptyState
            titleKey="chat_empty_title"
            bodyKey="chat_empty_body"
            action={{ labelKey: 'chat_empty_action', onClick: () => setInput(t('chat_placeholder')) }}
          />
        ) : (
          <ChatThread
            messages={state.messages}
            pending={state.pending}
            onRetry={retry}
            onQuote={(text) => setInput((prev) => (prev.trim() ? `${prev}\n${text}` : text))}
            renderActions={messageActions}
            renderExtra={renderExtra}
            selectable={armed !== null}
            picked={pinned.map((p) => p.text)}
            onPickPhrase={(sentence) =>
              setPinned((prev) => {
                const m = armed ?? 'exact';
                const exists = prev.findIndex((x) => x.text === sentence);
                if (exists >= 0) return prev.filter((_, i) => i !== exists);
                return [...prev, { text: sentence, mode: m }].slice(0, 8);
              })
            }
          />
        )}

        <ChatHints
          t={t}
          pickHintVisible={armed !== null}
          mode={mode}
          busyAction={busyAction}
          renderingId={null}
          actionError={actionError}
          gentle={flow.gentle ?? null}
          flowError={flow.error ?? null}
          flowPhase={flow.phase}
        />

        {/* ⭐ UI v2 步 13（設計 C05）：「手寫緊」生成卡（階段＋預計分鐘；無倒數；可繼續傾） */}
        {renderingId ? <GeneratingCard stage={genStage} /> : null}

        {flow.song && flow.phase !== 'lyrics' ? (
          <SongResult
            t={t}
            song={flow.song}
            busy={flow.busy}
            deviceId={deviceId}
            playSong={playSong}
            onPickVariant={(index) => void flow.pickVariant(index)}
          />
        ) : null}
      </div>


      {songFinished ? (
        <ContinuePrompt
          t={t}
          keepGoing={keepGoing}
          onToggleKeepGoing={() => setKeepGoing((v) => !v)}
          langBtn={langBtn}
        />
      ) : null}

      {state.errorKey ? (
        <p className="page__error" role="alert">
          {t(state.errorKey)}
          {state.errorDetail ? `（${state.errorDetail}）` : null}
        </p>
      ) : null}

      {/* 輸入框：底行左〔原話〕〔意思〕〔開始作歌〕…右〔▲〕；標記模式由頁面控制 */}
      <ChatComposer
        value={input}
        onChange={setInput}
        onSend={() => void handleSend()}
        busy={state.pending || busyAction}
        maxLength={4000}
        sendAriaKey="send_aria"
        /** ⭐ `decisions/027`：聊天模式換 placeholder（同一個輸入框，語氣唔同） */
        placeholderKey={mode === 'chat' ? 'chat_mode_placeholder' : 'chat_placeholder'}
        pinned={pinned}
        armed={armed}
        onArmedChange={setArmed}
        onMarkPhrase={(p) =>
          setPinned((prev) => (prev.some((x) => x.text === p.text && x.mode === p.mode) ? prev : [...prev, p].slice(0, 8)))
        }
        onUnpin={(i) => setPinned((prev) => prev.filter((_, idx) => idx !== i))}
        actionSlot={
          <button
            type="button"
            data-testid="start-song"
            disabled={!hasConversation || busyAction || state.pending}
            title={hasConversation ? undefined : t('chat_empty_title')}
            onClick={openStartSong}
            style={{ ...ROW_BTN, opacity: !hasConversation || busyAction ? 0.6 : 1 }}
          >
            {t('topbar_start_song')}
          </button>
        }
      />

      {/* ⭐ UI v2 步 5：① 語言／語體／風格、③b 換風格、之前嘅對話、改名／刪、加入播放清單
          —— 全部搬去 `features/chat/ChatSheets.tsx`（純搬，零行為改動） */}
      <ChatSheets
        t={t}
        sheet={sheet}
        setSheet={setSheet}
        busyAction={busyAction}
        langBtn={langBtn}
        runUnderstand={runUnderstand}
        songGenre={songGenre}
        pickGenre={pickGenre}
        genreOptions={genreOptions}
        genreQuick={genreQuick}
        toggleQuick={toggleQuick}
        songRegister={songRegister}
        setSongRegister={setSongRegister}
        registerOptions={registerOptions}
        songLang={songLang}
        setSongLang={setSongLang}
        langOptions={langOptions}
        pinnedCount={pinned.length}
        runRemake={runRemake}
        styleOptions={styleOptions}
        styleSelected={styleSelected}
        setStyleSelected={setStyleSelected}
        styleFree={styleFree}
        setStyleFree={setStyleFree}
        historyError={historyError}
        setHistoryError={setHistoryError}
        history={history}
        sessionId={sessionId}
        historyBusy={historyBusy}
        historyBusyId={historyBusyId}
        openSession={openSession}
        setRenameTarget={setRenameTarget}
        setDeleteTarget={setDeleteTarget}
        renameTarget={renameTarget}
        commitRename={commitRename}
        deleteTarget={deleteTarget}
        commitDelete={commitDelete}
        playlistSongId={playlistSongId}
        setPlaylistSongId={setPlaylistSongId}
        onLibraryReload={onLibraryReload}
      />
    </div>
  );
}

